import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  getMemberSelfBalance,
  getOwnPaymentDrafts,
  getPaymentDraftCatalogs,
  submitOwnPaymentDraft,
  type PaymentDraftCatalogs,
  type PaymentDraftItem,
} from '@urbanik/api-client';
import { AlertCircle, CheckCircle2, ReceiptText } from 'lucide-react';
import { apiBase } from './api-base';
import {
  CanonicalFileDropzone,
  CanonicalPdfViewer,
  SearchSelectField,
  SelectField,
  TextField,
} from '@urbanik/ui';
import { ecuadorFinancialInstitutions } from './ecuador-financial-institutions';

const scopes = [
  ['monthly_dues', 'Cuotas mensuales pendientes'],
  ['studies', 'Estudios'],
  ['area_regularization', 'Regularización de áreas'],
  ['all_pending', 'Todos los pendientes (más antiguos primero)'],
] as const;
const statusLabel: Record<string, string> = {
  submitted: 'En revisión',
  under_review: 'En revisión',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  applied: 'Aprobado y registrado',
  reversed: 'Reversado',
};
const longDateFormatter = new Intl.DateTimeFormat('es-EC', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'America/Guayaquil',
});
const longDateTimeFormatter = new Intl.DateTimeFormat('es-EC', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'America/Guayaquil',
});
const formatPaymentDate = (value: string) => {
  const parsed = new Date(`${value}T12:00:00-05:00`);
  return Number.isNaN(parsed.getTime()) ? 'Fecha no disponible' : longDateFormatter.format(parsed);
};
const formatRegisteredAt = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? 'Fecha no disponible'
    : longDateTimeFormatter.format(parsed);
};

export function MemberPaymentDraftForm({
  memberId,
  memberNationalId,
  memberName,
  showHistory = true,
  canApprove = false,
}: {
  memberId?: string;
  memberNationalId?: string;
  memberName?: string;
  showHistory?: boolean;
  canApprove?: boolean;
} = {}) {
  const submissionInFlight = useRef(false);
  const [catalogs, setCatalogs] = useState<PaymentDraftCatalogs | null>(null);
  const [items, setItems] = useState<PaymentDraftItem[]>([]);
  const [method, setMethod] = useState('bank_transfer');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [originBank, setOriginBank] = useState('');
  const [reference, setReference] = useState('');
  const [memberObservation, setMemberObservation] = useState('');
  const [scope, setScope] = useState('monthly_dues');
  const [destination, setDestination] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewError, setPreviewError] = useState('');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const clearFieldError = (field: string) =>
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  const [busy, setBusy] = useState(false);
  const [ownIdentity, setOwnIdentity] = useState<{
    nationalId: string;
    displayName: string;
  } | null>(null);
  const refresh = () =>
    memberId
      ? Promise.resolve(setItems([]))
      : getOwnPaymentDrafts(apiBase).then((value) => setItems(value.items));
  useEffect(() => {
    setPreviewError('');
    if (!file) {
      setPreviewUrl('');
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);
  useEffect(() => {
    if (!memberId) {
      void getMemberSelfBalance(apiBase)
        .then(({ member }) => setOwnIdentity(member))
        .catch(() => undefined);
    }
  }, [memberId]);
  useEffect(() => {
    const drafts = memberId
      ? Promise.resolve({ items: [] as PaymentDraftItem[] })
      : getOwnPaymentDrafts(apiBase);
    Promise.all([getPaymentDraftCatalogs(apiBase, memberId), drafts])
      .then(([nextCatalogs, draftResult]) => {
        setCatalogs(nextCatalogs);
        setItems(draftResult.items);
        setMethod(nextCatalogs.methods[0]?.code ?? '');
        setDestination(nextCatalogs.destinationAccounts[0] ?? '');
      })
      .catch((reason: Error) => setError(reason.message));
  }, [memberId]);
  const selectedMethod = catalogs?.methods.find((item) => item.code === method);
  const identity = {
    nationalId: memberNationalId ?? ownIdentity?.nationalId ?? 'socio',
    displayName: memberName ?? ownIdentity?.displayName ?? 'sin-nombre',
  };
  const fileStem = [identity.nationalId, identity.displayName, reference || 'sin-referencia']
    .map((part) =>
      part
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toUpperCase(),
    )
    .filter(Boolean)
    .join('_');
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');
    const missing: Record<string, string> = {};
    if (!method) missing.method = 'Selecciona el tipo de pago.';
    if (!amount || !/^\d+(\.\d{1,2})?$/.test(amount) || Number(amount) <= 0)
      missing.amount = 'Ingresa un valor mayor que cero, con máximo dos decimales.';
    if (!date) missing.date = 'Selecciona la fecha del comprobante.';
    if (reference.trim().length < 3)
      missing.reference = 'Ingresa el número de comprobante o referencia (mínimo 3 caracteres).';
    if (selectedMethod?.requiresOriginBank && !originBank)
      missing.originBank = 'Selecciona el banco o cooperativa de origen.';
    if (!destination) missing.destination = 'Selecciona la cuenta receptora.';
    if (!scope) missing.scope = 'Selecciona qué deseas pagar.';
    if (!file) missing.file = 'Adjunta una imagen, toma una foto o carga el PDF del comprobante.';
    setFieldErrors(missing);
    if (Object.keys(missing).length) {
      setError('Faltan datos obligatorios. Revisa los campos señalados.');
      const firstId = Object.keys(missing)[0];
      requestAnimationFrame(() => document.getElementById(`payment-${firstId}`)?.focus());
      return;
    }
    if (!file || submissionInFlight.current) return;
    submissionInFlight.current = true;
    setBusy(true);
    try {
      const extension = file.name.toLowerCase().endsWith('.pdf')
        ? 'pdf'
        : file.type === 'image/png'
          ? 'png'
          : 'jpg';
      const evidenceFile = new File([file], `${fileStem}.${extension}`, { type: file.type });
      const draft = await submitOwnPaymentDraft(apiBase, {
        methodCode: method,
        amount,
        transactionDate: date,
        originBank,
        transactionReference: reference,
        allocationScope: scope,
        destinationAccount: destination,
        memberObservation,
        ...(memberId ? { memberId } : {}),
        file: evidenceFile,
      });
      window.dispatchEvent(new CustomEvent('urbanik:payment-draft-submitted'));
      setNotice(
        memberId
          ? 'Comprobante registrado para revisión manual. Revisa la evidencia y ajusta la distribución antes de aprobarlo.'
          : 'Comprobante registrado. El saldo no cambiará hasta que tesorería o administración lo apruebe.',
      );
      setAmount('');
      setReference('');
      setOriginBank('');
      setMemberObservation('');
      setFile(null);
      await refresh();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      submissionInFlight.current = false;
      setBusy(false);
    }
  };
  if (!catalogs && !error)
    return (
      <section className="member-payment">
        <p>Cargando registro de pagos…</p>
      </section>
    );
  return (
    <section className="member-payment" id="registrar-pago">
      <header>
        <div>
          <span>PAGOS</span>
          <h2>Registrar comprobante</h2>
        </div>
        <small>
          {memberId && canApprove
            ? 'Registro administrativo: requiere revisión y distribución manual antes de aprobar.'
            : 'Requiere aprobación antes de afectar tu saldo.'}
        </small>
      </header>
      {error ? (
        <p className="member-payment__message member-payment__message--error">
          <AlertCircle />
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="member-payment__message member-payment__message--success">
          <CheckCircle2 />
          {notice}
        </p>
      ) : null}
      {catalogs ? (
        <form onSubmit={submit} className="member-payment__form" noValidate>
          {Object.values(fieldErrors).filter(Boolean).length ? (
            <div className="member-payment__validation-summary" role="alert">
              <strong>Completa los datos obligatorios:</strong>
              <ul>
                {Object.entries(fieldErrors)
                  .filter(([, message]) => Boolean(message))
                  .map(([field, message]) => (
                    <li key={field}>{message}</li>
                  ))}
              </ul>
            </div>
          ) : null}
          <SelectField
            id="payment-method"
            label="Tipo de pago"
            required
            error={fieldErrors.method}
            value={method}
            onValueChange={(value) => {
              setMethod(value);
              clearFieldError('method');
            }}
            options={catalogs.methods.map((item) => ({ value: item.code, label: item.name }))}
          />
          <TextField
            id="payment-amount"
            label="Valor del comprobante (USD)"
            required
            error={fieldErrors.amount}
            inputMode="decimal"
            pattern="\d+(\.\d{1,2})?"
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              clearFieldError('amount');
            }}
            placeholder="0.00"
          />
          <TextField
            id="payment-date"
            label="Fecha del comprobante"
            required
            error={fieldErrors.date}
            type="date"
            max={new Date().toISOString().slice(0, 10)}
            value={date}
            onChange={(event) => {
              setDate(event.target.value);
              clearFieldError('date');
            }}
          />
          <TextField
            id="payment-reference"
            label="Número de comprobante o referencia"
            required
            error={fieldErrors.reference}
            minLength={3}
            maxLength={160}
            value={reference}
            onChange={(event) => {
              setReference(event.target.value);
              clearFieldError('reference');
            }}
          />
          {selectedMethod?.requiresOriginBank ? (
            <SearchSelectField
              id="payment-originBank"
              label="Banco de origen"
              required
              error={fieldErrors.originBank}
              value={originBank}
              placeholder="Buscar banco o cooperativa"
              options={ecuadorFinancialInstitutions.map(([name, type]) => ({
                value: name,
                label: name,
                meta: `${type} · Ecuador`,
              }))}
              onValueChange={(value) => {
                setOriginBank(value);
                clearFieldError('originBank');
              }}
            />
          ) : null}
          <SelectField
            id="payment-destination"
            label="Cuenta receptora"
            required
            error={fieldErrors.destination}
            value={destination}
            onValueChange={(value) => {
              setDestination(value);
              clearFieldError('destination');
            }}
            placeholder="Selecciona una cuenta"
            options={catalogs.destinationAccounts.map((item) => ({ value: item, label: item }))}
          />
          <label className="member-payment__field">
            <span>
              Observación <small>(opcional)</small>
            </span>
            <textarea
              id="payment-observation"
              rows={4}
              maxLength={1000}
              value={memberObservation}
              onChange={(event) => setMemberObservation(event.target.value)}
              placeholder="Ej.: corresponde a las cuotas de enero a marzo."
            />
            <small>{memberObservation.length}/1000 caracteres</small>
          </label>
          <SelectField
            id="payment-scope"
            label="¿Qué deseas pagar?"
            required
            error={fieldErrors.scope}
            value={scope}
            onValueChange={(value) => {
              setScope(value);
              clearFieldError('scope');
            }}
            options={scopes.map(([value, label]) => ({ value, label }))}
          />
          <div className="member-payment__evidence">
            <CanonicalFileDropzone
              label="Imagen o PDF del comprobante"
              file={file}
              onFileChange={(value) => {
                setFile(value);
                clearFieldError('file');
              }}
              onError={setError}
              accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
              maxSizeBytes={10 * 1024 * 1024}
              helpText="JPG, PNG o PDF; máximo 10 MB."
              allowCamera
              cameraFileName={fileStem}
            />
            {fieldErrors.file ? (
              <p className="member-payment__field-error" role="alert">
                {fieldErrors.file}
              </p>
            ) : null}
            <aside className="member-payment__preview" aria-label="Vista previa del comprobante">
              {file &&
              (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) ? (
                <CanonicalPdfViewer file={file} fileName={file.name} />
              ) : file ? (
                <img
                  src={previewUrl}
                  alt="Vista previa del comprobante"
                  onError={() =>
                    setPreviewError(
                      'No se pudo mostrar la imagen. Verifica que sea un JPG o PNG válido.',
                    )
                  }
                />
              ) : (
                <p>La vista previa aparecerá aquí.</p>
              )}
              {previewError ? (
                <p className="member-payment__preview-error">{previewError}</p>
              ) : null}
            </aside>
          </div>
          {!catalogs.destinationAccounts.length ? (
            <p className="member-payment__warning">
              Administración debe configurar al menos una cuenta receptora en{' '}
              <code>URBANIK_PAYMENT_DESTINATION_ACCOUNTS</code>.
            </p>
          ) : null}
          <button type="submit" disabled={busy || !catalogs.destinationAccounts.length}>
            {busy ? 'Registrando…' : 'Enviar a revisión'}
          </button>
        </form>
      ) : null}
      {showHistory ? (
        <div className="member-payment__history">
          <h3>Mis comprobantes registrados</h3>
          {items.length ? (
            <ul>
              {items.map((item) => (
                <li key={item.id}>
                  <ReceiptText />
                  <span>
                    <strong>
                      {item.methodName} · USD {item.amount}
                    </strong>
                    <small>
                      Fecha del comprobante: {formatPaymentDate(item.transactionDate)} · Ref.{' '}
                      {item.transactionReference}
                      <br />
                      Registrado: {formatRegisteredAt(item.registeredAt)}
                    </small>
                  </span>
                  <b>{statusLabel[item.status] ?? item.status}</b>
                </li>
              ))}
            </ul>
          ) : (
            <p>Aún no has registrado comprobantes.</p>
          )}
        </div>
      ) : null}
    </section>
  );
}
